/**
 * Coverflow 3D — rigid **yaw** only (`rotateY`), no synthetic `rotateX` (avoids corner fighting).
 *
 * **Why this file keeps changing shape**
 * - The slide **shell** uses `filter: blur()` for depth. That **breaks `preserve-3d`**, so
 *   **`perspective` on the Embla viewport does not reach the card** — `rotateY` looks flat.
 * - So **`perspective()` has to live on the card `transform`** next to `rotateY` (same as `TiltCard`).
 * - **`perspective` + `rotateY` + `transform-origin: 50% 50%`** puts the rotation axis down the
 *   **middle of the portrait** → reads as a **book spine / half-fold**.
 * - Fix: **hinge near the inner vertical edge** (toward the carousel centre), **blended smoothly**
 *   with offset so we never sit at “mid-face pivot” unless the slide is essentially dead-centre.
 *
 * **Important:** `filter: blur()` must not sit on the same layer as the 3D transform (see carousel).
 * **Clip + radius** stay on a non-transformed child under `[data-coverflow-card-inner]`.
 */

export const COVERFLOW_CARD_W_PX = 310;
export const COVERFLOW_CARD_GAP_PX = 96;
export const COVERFLOW_STRIDE_PX = COVERFLOW_CARD_W_PX + COVERFLOW_CARD_GAP_PX;

const TILT_MAX_DEG = 16;
/** With the card hinge, a slightly longer lens keeps the face from “caving” at the middle. */
export const COVERFLOW_TILT_PERSPECTIVE_PX = 1260;

/** How far (in %-points from 50%) the seam hinge moves at full |u| — ~90.5% / ~9.5% at ±1. */
const HINGE_SPAN_PCT = 40.5;

/** Scale at ~|u|≈1 (first neighbour); centre `u≈0` stays 1. */
const COVERFLOW_SIDE_SCALE = 0.87;

export type CoverflowVisuals = {
  /** `perspective()` + `rotateY` + `scale` on the card — shell `filter` blocks viewport-only perspective. */
  cardTransform: string;
  /** Uniform scale factor (also embedded in `cardTransform`) for reduced-motion branches. */
  cardScale: number;
  transformOrigin: string;
  /** Applied to the Embla slide node (wrapper) — never combined with `cardTransform` on one layer. */
  shellOpacity: number;
  shellFilter: string;
  zIndex: number;
  boxShadow: string;
};

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/**
 * Slide indices whose centers are closest to `viewportCenterX` (Framer-style: exactly `count` cards stay fully sharp).
 * Tie-break on lower index for stable ordering when distances match.
 */
export function pickClosestSlideIndices(
  centers: number[],
  viewportCenterX: number,
  count: number
): Set<number> {
  const n = centers.length;
  if (n === 0) return new Set();
  const take = Math.min(Math.max(1, Math.floor(count)), n);
  const dists = centers.map((cx) => Math.abs(cx - viewportCenterX));
  const order = centers.map((_, i) => i).sort((a, b) => {
    const da = dists[a]!;
    const db = dists[b]!;
    if (da !== db) return da - db;
    return a - b;
  });
  return new Set(order.slice(0, take));
}

/** 0→1 ease — keeps hinge gentle near centre, full by |u|≈1. */
function hingeBlend(tRaw: number): number {
  const t = clamp(tRaw, 0, 1);
  return t * t * (3 - 2 * t);
}

/**
 * Vertical axis for `rotateY`: near **inner** card edge (toward viewport centre), not down the face.
 * Smooth in `|u|` so we do not snap between “fold” and “flat” at a hard threshold.
 */
function coverflowTransformOrigin(u: number): string {
  const au = Math.abs(u);
  if (au < 1e-4) return "50% 50%";
  const t = hingeBlend(Math.min(1, au * 1.08));
  const span = HINGE_SPAN_PCT * t;
  if (u < 0) {
    const x = 50 + span;
    return `${x.toFixed(2)}% 50%`;
  }
  const x = 50 - span;
  return `${x.toFixed(2)}% 50%`;
}

/**
 * @param offsetSlides — `(slideCenterX - viewportCenterX) / COVERFLOW_STRIDE_PX`
 */
export function computeCoverflowVisuals(
  offsetSlides: number,
  mode: "desktop" | "mobile"
): CoverflowVisuals {
  const k = mode === "mobile" ? 0.94 : 1;
  const maxTilt = TILT_MAX_DEG * k;

  const u = clamp(offsetSlides, -1, 1);

  const nx = 0.5 - 0.5 * u;
  const rotateYDeg = (nx - 0.5) * 2 * maxTilt;
  const rotateXDeg = 0;

  const au = Math.abs(u);
  const scaleT = hingeBlend(Math.min(1, au));
  const cardScale = 1 - (1 - COVERFLOW_SIDE_SCALE) * scaleT;

  const absSlides = Math.abs(offsetSlides);
  const depth = Math.min(absSlides * 0.9, 1);
  const blurPx = Math.min(absSlides * 1.25 * k, 6);
  const brightness = clamp(1 - depth * 0.09, 0.88, 1);
  const shellOpacity = clamp(1 - depth * 0.2, 0.52, 1);
  const zIndex = Math.round(200 - Math.min(1, depth * 4) * 36);

  const cardTransform = `perspective(${COVERFLOW_TILT_PERSPECTIVE_PX}px) rotateX(${rotateXDeg.toFixed(2)}deg) rotateY(${rotateYDeg.toFixed(2)}deg) scale(${cardScale.toFixed(4)})`;
  const shellFilter = `blur(${blurPx.toFixed(2)}px) brightness(${brightness.toFixed(3)})`;

  const boxShadow =
    depth < 0.12
      ? "0 24px 70px rgba(0,0,0,0.45), 0 0 50px rgba(255, 255, 255, 0.05)"
      : "0 14px 36px rgba(0,0,0,0.35)";

  return {
    cardTransform,
    cardScale,
    transformOrigin: coverflowTransformOrigin(u),
    shellOpacity,
    shellFilter,
    zIndex,
    boxShadow,
  };
}
